import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { withMemberGate } from "@/lib/with-member-gate";
import {
  CLOSING_BLOCKS,
  DAYS,
  INTRO_SECTIONS,
  NO_STREAK_NOTICE,
  PHASES,
  SAFETY_NOTICE,
} from "@/lib/secure-within";
import { SecureWithinProvider, useSecureWithin } from "@/components/secure-within/state";
import { SecureWithinBlock } from "@/components/secure-within/Blocks";
import { ProgressBar } from "@/components/breaking-free/Shared";

export const Route = createFileRoute("/secure-within/")({
  head: () => ({
    meta: [
      { title: "Secure Within — 30-Day Attachment Healing Journey | Recovery Trauma™" },
      {
        name: "description",
        content:
          "A self-paced, trauma-informed journey to understand your attachment patterns, explore where they came from, recognise your triggers and practise more secure ways of connecting.",
      },
      { property: "og:title", content: "Secure Within — 30-Day Attachment Healing Journey" },
      {
        property: "og:description",
        content:
          "Understand your attachment patterns, recognise your triggers and practise more secure ways of connecting — at your own pace.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: withMemberGate(SecureWithinPage, "Secure Within"),
});

function SecureWithinPage() {
  return (
    <SecureWithinProvider>
      <Inner />
    </SecureWithinProvider>
  );
}

function Inner() {
  const { loading, error, refetch, completedDays, currentDay, progress, begin } =
    useSecureWithin();
  const [tab, setTab] = useState<"journey" | "before" | "after">("journey");
  const started = Boolean(progress);
  const finished = completedDays.length >= 30;

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-10">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-xl px-4 py-16 text-center">
        <p className="text-base">Something didn't load correctly.</p>
        <Button className="mt-4 min-h-[48px]" onClick={refetch}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-16 pt-8 sm:pt-12">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
        30-day guided journey
      </p>
      <h1 className="mt-2 text-3xl font-semibold sm:text-4xl">🤍 Secure Within</h1>
      <p className="mt-1 text-lg text-muted-foreground">
        A 30-Day Attachment Healing Journey
      </p>
      <p className="mt-4 text-[15px] leading-relaxed text-foreground/85">
        Understand your attachment patterns, explore where they came from, recognise your triggers
        and practise more secure ways of connecting. Thirty stages, at your own pace — these are
        patterns for self-exploration, never diagnoses or fixed identities.
      </p>

      <div className="mt-6 rounded-3xl border border-gold/30 bg-card p-5">
        <ProgressBar done={completedDays.length} total={30} />
        <p className="mt-3 text-xs text-muted-foreground">{NO_STREAK_NOTICE}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button asChild className="min-h-[48px]">
            <Link to="/secure-within/day/$day" params={{ day: String(currentDay) }}>
              {completedDays.length === 0 ? "Start the journey" : `Continue — Day ${currentDay}`}
            </Link>
          </Button>
          <Button asChild variant="outline" className="min-h-[48px]">
            <Link to="/secure-within/journey">View my reflections</Link>
          </Button>
          {!started ? (
            <Button variant="ghost" className="min-h-[48px]" onClick={() => void begin()}>
              Save my place
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-8 flex gap-2 overflow-x-auto pb-1">
        {(
          [
            ["journey", "The 30 stages"],
            ["before", "Before you begin"],
            ["after", "Your journey continues"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`min-h-[44px] whitespace-nowrap rounded-full border px-4 text-sm ${
              tab === key
                ? "border-gold bg-gold-light/50 font-semibold text-[var(--gold-deep)]"
                : "border-border bg-background text-foreground/80"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-8">
        {tab === "journey" ? (
          <JourneyGrid completedDays={completedDays} currentDay={currentDay} />
        ) : null}
        {tab === "before" ? <BeforeYouBegin /> : null}
        {tab === "after" ? <AfterThirty finished={finished} /> : null}
      </div>

      <div className="mt-10 rounded-2xl border border-border bg-muted/40 p-4 text-xs leading-relaxed text-muted-foreground">
        <p>{SAFETY_NOTICE}</p>
        <p className="mt-2">
          If you need urgent support,{" "}
          <Link to="/crisis" className="underline">
            crisis help is here
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function JourneyGrid({
  completedDays,
  currentDay,
}: {
  completedDays: number[];
  currentDay: number;
}) {
  return (
    <div className="space-y-8">
      {PHASES.map((phase, index) => (
        <section key={phase.key}>
          <h2 className="text-lg font-semibold">
            Phase {index + 1} — {phase.name}
          </h2>
          <p className="text-sm text-muted-foreground">{phase.range}</p>
          <p className="mt-2 text-sm text-foreground/80">{phase.blurb}</p>
          <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {DAYS.filter((d) => d.day >= phase.from && d.day <= phase.to).map((d) => {
              const done = completedDays.includes(d.day);
              const current = d.day === currentDay;
              return (
                <li key={d.day}>
                  <Link
                    to="/secure-within/day/$day"
                    params={{ day: String(d.day) }}
                    className={`flex min-h-[96px] flex-col justify-between rounded-2xl border p-3 transition ${
                      done
                        ? "border-emerald-300/70 bg-emerald-50/50 dark:bg-emerald-950/20"
                        : current
                          ? "border-gold bg-gold-light/40"
                          : "border-border bg-background hover:bg-muted"
                    }`}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {done ? "✓" : current ? "●" : "○"} Day {d.day}
                    </span>
                    <span className="mt-2 text-sm font-medium leading-snug">
                      {d.emoji} {d.title}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function BeforeYouBegin() {
  const [open, setOpen] = useState<string | null>(INTRO_SECTIONS[0]?.key ?? null);
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        A short introduction to attachment. Read it in any order — or come back to it later.
      </p>
      {INTRO_SECTIONS.map((section) => (
        <div key={section.key} className="rounded-3xl border border-border bg-background">
          <button
            type="button"
            className="flex min-h-[56px] w-full items-center justify-between gap-3 px-5 text-start"
            onClick={() => setOpen(open === section.key ? null : section.key)}
          >
            <span className="text-sm font-semibold">
              {section.emoji} {section.title}
            </span>
            <span className="text-muted-foreground">{open === section.key ? "–" : "+"}</span>
          </button>
          {open === section.key ? (
            <div className="space-y-4 px-4 pb-5">
              {section.blocks.map((block) => (
                <SecureWithinBlock key={block.key} block={block} day={null} />
              ))}
              {section.safety ? (
                <p className="rounded-2xl bg-muted/50 p-3 text-xs text-muted-foreground">
                  {section.safety}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function AfterThirty({ finished }: { finished: boolean }) {
  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-gold/30 bg-gold-light/25 p-5">
        <h2 className="text-lg font-semibold">
          Thirty stages explored. Your healing doesn't have a deadline.
        </h2>
        <p className="mt-2 text-sm text-foreground/80">
          {finished
            ? "You've walked through all thirty stages. Nothing closes — everything here stays yours."
            : "This part is always open, whether you're on Stage 3 or Stage 30. Nothing is locked."}
        </p>
      </div>
      {CLOSING_BLOCKS.map((block) => (
        <SecureWithinBlock key={block.key} block={block} day={null} />
      ))}
      <div className="flex flex-wrap gap-3">
        {[
          { to: "/grounding", label: "Continue regulating" },
          { to: "/inner-child", label: "Inner Child work" },
          { to: "/trigger-journal", label: "My Trigger Journal" },
          { to: "/journal", label: "My Recovery Journal" },
        ].map((l) => (
          <Button key={l.to} asChild variant="outline" className="min-h-[48px]">
            <Link to={l.to as never}>{l.label}</Link>
          </Button>
        ))}
        <Button asChild variant="outline" className="min-h-[48px]">
          <Link to="/secure-within/journey">My Secure Within Journey</Link>
        </Button>
      </div>

      <div className="rounded-3xl border border-border bg-background p-5">
        <h3 className="text-base font-semibold">Exploring a trauma bond?</h3>
        <p className="mt-2 text-sm text-foreground/85">
          💔 Breaking Free is a 30-day journey into the bond itself — the cycle, the patterns and
          rebuilding self-trust.
        </p>
        <Button asChild variant="outline" className="mt-4 min-h-[48px]">
          <Link to="/breaking-free">Explore Breaking Free</Link>
        </Button>
      </div>
    </div>
  );
}
